import {Injectable} from '@angular/core';
import {LookupService} from '../../common/service/lookup.service';
import {SpatialUtilService} from '../services/spatial-util.service';
import {LayerUtilService} from './layer-util.service';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
/* eslint-disable max-len */
@Injectable({
    providedIn: 'root'
})
export class LineLayerService {
    constructor(
        private lookupService: LookupService,
        private spatialUtilService: SpatialUtilService,
        private layerUtilService: LayerUtilService,
        private sanitizer: DomSanitizer
    ) {}

    private layerId = 'lineFeatureLayer_layer';
    private layerSelector = `#${this.layerId}`;

    private featureClass = 'line';
    private featureIdAttrName = this.layerUtilService.getFeatureIdAttrName(this.featureClass);
    private featureTypeAttrName = this.layerUtilService.getFeatureTypeAttrName(this.featureClass);
    private featureConstructionStyleNodeId = this.layerUtilService.getFeatureNodeId(this.featureClass, 0);

    private domParser = new DOMParser();

    private getAllLineSymbologyLookups = () => this.lookupService.getLookups('lineSymbology').filter(l => ![24, 96].includes(l.type));

    private getLineSymbologyLookup(lineType) {
        const lookupViewMode = this.spatialUtilService.getMapBackground() === 'white' ? 'White' : 'Black';
        return this.getAllLineSymbologyLookups().find(l => l.type === lineType && l.viewMode === lookupViewMode);
    }

    private getPathCoordinates(path) {
        return path.getAttribute('path').slice(2).replace(/\s/g, ',').split(',').map(parseFloat);
    }

    private updateDashPath(evt, dashPath) {
        if (!dashPath) {
            return;
        }

        const coordinates = this.getPathCoordinates(evt.node);
        dashPath.setAttribute('d', `M ${coordinates[0]} ${coordinates[1]} ${coordinates[2]} ${coordinates[3]}`);
    }

    private updatePatternPath(evt, pattern, patternPath) {
        if (!patternPath || !pattern) {
            return;
        }

        const lineSymbologyLookup = this.getLineSymbologyLookup(evt.graphic.attributes.type);

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [viewBoxMinX, viewBoxMinY, viewBoxWidth, viewBoxHeight] = lineSymbologyLookup.svgViewBox.split(' ').map(v => parseFloat(v));
        const delimiter = parseFloat(lineSymbologyLookup.delimiter);

        const coordinates = this.getPathCoordinates(evt.node);

        const x1 = coordinates[0];
        const y1 = coordinates[1];
        const x2 = coordinates[2];
        const y2 = coordinates[3];

        const xDelta = x2 - x1;
        const yDelta = y2 - y1;

        if (xDelta === 0 && yDelta === 0) {
            return;
        }

        let theta = 0;

        let xOffset = 0;
        let yOffset = 0;

        if (xDelta === 0) {
            xOffset = 1;
            theta = 0;
        } else if (yDelta === 0) {
            yOffset = 1;
            theta = Math.PI / 2;
        } else {
            theta = Math.atan2(yDelta, xDelta) + Math.PI / 2;
            xOffset = Math.cos(theta);
            yOffset = Math.sin(theta);
        }

        //Review: Always specify delimiter - it's the multiplier of both dash array and view box.
        const fontSize = delimiter * viewBoxWidth;
        const scale = fontSize * this.layerUtilService.getPixelScaleFactor();

        const lineLength = Math.sqrt(Math.pow(xDelta, 2) + Math.pow(yDelta, 2));

        //When pattern view box sits to one side of the line, truncate the pattern path to fit a whole number of pattern tiles.
        //When pattern view box sits over the center of the line, do not truncate the pattern path.
        const adjustedLineLength = viewBoxHeight === -2 * viewBoxMinY ? lineLength : lineLength - (lineLength % scale);
        const adjustedX2 = x1 + (xDelta * adjustedLineLength) / lineLength;
        const adjustedY2 = y1 + (yDelta * adjustedLineLength) / lineLength;

        const offsetFactorStart = viewBoxMinY / viewBoxHeight;
        const offsetFactorEnd = offsetFactorStart + 1;

        const px1 = x1 + offsetFactorStart * xOffset * scale;
        const py1 = y1 + offsetFactorStart * yOffset * scale;
        const px2 = adjustedX2 + offsetFactorStart * xOffset * scale;
        const py2 = adjustedY2 + offsetFactorStart * yOffset * scale;
        const px3 = adjustedX2 + offsetFactorEnd * xOffset * scale;
        const py3 = adjustedY2 + offsetFactorEnd * yOffset * scale;
        const px4 = x1 + offsetFactorEnd * xOffset * scale;
        const py4 = y1 + offsetFactorEnd * yOffset * scale;

        const patternWidth = scale;
        const patternHeight = scale;

        pattern.setAttribute('width', `${patternWidth}`);
        pattern.setAttribute('height', `${patternHeight}`);

        const patternRotation = (180 * (theta - Math.PI / 2)) / Math.PI;
        pattern.setAttribute('patternTransform', `translate(${px1},${py1}) rotate(${patternRotation}, 0, 0)`);

        patternPath.setAttribute('d', `M ${px1} ${py1} ${px2} ${py2} ${px3} ${py3} ${px4} ${py4}`);
    }

    svgStyle;
    trustedSvgStyle;

    getSvgStyle(): SafeHtml {
        const scaleFactor = this.layerUtilService.getPixelScaleFactor();
        if (scaleFactor === 0) {
            return '';
        }

        const strokeWidthScaleFactor = this.layerUtilService.getStrokeWidthPixelScaleFactor(scaleFactor);

        const mapBackgroundColor = this.spatialUtilService.getMapBackground();

        const showFeatures = this.layerUtilService.showFeatures();

        const newSvgStyle = [
            `${this.layerSelector} > path[${this.featureTypeAttrName}] + g > path:first-of-type + pattern {${showFeatures ? '' : 'display:none;'}}`,
            `${this.layerSelector} > path[${this.featureTypeAttrName}] + g > path:first-of-type + pattern + path {pointer-events:none;}`,
            `${this.layerSelector} > path[${this.featureTypeAttrName}]:hover + g:not(#${this.featureConstructionStyleNodeId}) {animation: feature-symbol-hover 0.3s ease-in-out alternate none infinite;}`,
            `${this.layerSelector} > path[${this.featureTypeAttrName}]:not([data-hidden]):not([data-selected]):hover + g:not(#${this.featureConstructionStyleNodeId}) > path:first-of-type {stroke:${this.layerUtilService.getHoverColor()};}`,
            `${this.layerSelector} > path[${this.featureTypeAttrName}]:not([data-hidden]):not([data-selected]):hover + g:not(#${this.featureConstructionStyleNodeId}) > pattern > * {stroke:${this.layerUtilService.getHoverColor()};}`,
            `${this.layerSelector} > path[${this.featureTypeAttrName}][data-selected] + g > path:first-of-type {stroke:${this.layerUtilService.getSelectedColor()}; stroke-opacity:1.0}`,
            `${this.layerSelector} > path[${this.featureTypeAttrName}][data-selected] + g > path:first-of-type + pattern > * {stroke:${this.layerUtilService.getSelectedColor()};}`,
            `${this.layerSelector} > path[${this.featureTypeAttrName}][data-hidden]:not([data-selected]) + g > path:first-of-type {stroke:${this.layerUtilService.getHiddenColor()};}`,
            `${this.layerSelector} > path[${this.featureTypeAttrName}][data-hidden]:not([data-selected]) + g > path:first-of-type + pattern > * {stroke:${this.layerUtilService.getHiddenColor()};}`,
            ...this.getAllLineSymbologyLookups()
                .filter(l => l.viewMode.toLowerCase() === mapBackgroundColor)
                .map(l => {
                    const stroke = showFeatures ? `rgb(${l.color})` : 'transparent';
                    const strokeLineCap = l.lineCap;
                    const strokeLineJoin = l.lineJoin;

                    const pointerEventsLineStrokeWidth =
                        l.width * this.layerUtilService.getPointerEventsLineWidthPixelScaleFactor(strokeWidthScaleFactor);

                    const pointerEventsLineProperties = [
                        `stroke:${stroke};`,
                        `stroke-linecap:${strokeLineCap};`,
                        `stroke-linejoin:${strokeLineJoin};`,
                        `stroke-miterlimit:1;`,
                        `stroke-width: ${pointerEventsLineStrokeWidth};`,
                        `stroke-opacity:0.0;`,
                        `fill-opacity:1.0;`
                    ];

                    let strokeDashArray;

                    if (l.dasharray) {
                        const delimiter = parseFloat(l.delimiter);
                        const dashValueMultiplier = scaleFactor * delimiter;
                        strokeDashArray = l.dasharray
                            .split(',')
                            .map(v => `${parseFloat(v) * dashValueMultiplier}`)
                            .join();
                    } else {
                        strokeDashArray = 'none';
                    }

                    const dashLineStrokeWidth = l.width * strokeWidthScaleFactor;

                    const dashLineProperties = [
                        `stroke:${stroke};`,
                        `stroke-linecap:${strokeLineCap};`,
                        `stroke-linejoin:${strokeLineJoin};`,
                        `stroke-miterlimit:1;`,
                        `stroke-width: ${dashLineStrokeWidth};`,
                        `stroke-opacity:${l.type === 95 ? 0.0 : 1.0};`,
                        `fill-opacity:1.0;`,
                        `stroke-dasharray: ${strokeDashArray};`,
                        `pointer-events: none;`
                    ];

                    if (l.arrowLeft || l.arrowRight) {
                        dashLineProperties.push(`marker-end: url(#line-marker-${l.type});`);
                    }

                    const styles = [
                        `${this.layerSelector} > path[${this.featureTypeAttrName}="${l.type}"] {${pointerEventsLineProperties.join('')}}`,
                        `${this.layerSelector} > path[${this.featureTypeAttrName}="${l.type}"] + g > path:first-of-type {${dashLineProperties.join('')}}`
                    ];

                    if (l.arrowLeft || l.arrowRight) {
                        styles.push(
                            `${this.layerSelector} > path[${this.featureTypeAttrName}="${l.type}"]:hover + g > path:first-of-type {marker-end: url(#line-marker-${l.type}-hover);}`,
                            `${this.layerSelector} > path[${this.featureTypeAttrName}="${l.type}"][data-hidden]:not([data-selected]) + g > path:first-of-type {marker-end: url(#hidden-line-marker-${l.type});}`,
                            `${this.layerSelector} > path[${this.featureTypeAttrName}="${l.type}"][data-hidden]:not([data-selected]):hover + g > path:first-of-type {marker-end: url(#hidden-line-marker-${l.type}-hover);}`,
                            `${this.layerSelector} > path[${this.featureTypeAttrName}="${l.type}"][data-selected] + g > path:first-of-type {marker-end: url(#selected-line-marker-${l.type});}`,
                            `${this.layerSelector} > path[${this.featureTypeAttrName}="${l.type}"][data-selected]:hover + g > path:first-of-type {marker-end: url(#selected-line-marker-${l.type}-hover);}`
                        );
                    }

                    return styles.join('\n');
                })
        ].join('\n');

        if (newSvgStyle === this.svgStyle) {
            return this.trustedSvgStyle;
        }
        this.svgStyle = newSvgStyle;
        this.trustedSvgStyle = this.sanitizer.bypassSecurityTrustHtml(this.svgStyle);

        return this.trustedSvgStyle;
    }

    private createMarker(l, scaleFactor, stroke, fill, markerId) {
        const arrowWidth = l.arrowWidth;
        const arrowHeight = l.arrowHeight;
        const markerWidth = arrowWidth;
        const markerHeight = arrowHeight + 1;
        const markerUnits = 'strokeWidth'; //Defines the coordinate system for the markerWidth, markerHeight

        const refX = arrowWidth + l.width;
        const refY = arrowHeight / 2.0;

        const strokeWidthScaleFactor = this.layerUtilService.getStrokeWidthPixelScaleFactor(scaleFactor);
        const strokeWidthAttr = l.arrowLineWidth ? `stroke-width="${l.arrowLineWidth * strokeWidthScaleFactor}"` : '';

        const strokeLineCap = l.arrowLineCap;
        const strokeLineJoin = l.arrowLineJoin;

        let strokeDashArray;
        if (l.arrowDasharray) {
            const dashValueMultiplier = scaleFactor * parseFloat(l.arrowDelimiter);
            strokeDashArray = l.arrowDasharray
                .split(',')
                .map(v => `${parseFloat(v) * dashValueMultiplier}`)
                .join();
        } else {
            strokeDashArray = 'none';
        }

        return `<marker id="${markerId}"
                        refX="${refX}"
                        refY="${refY}"
                        markerWidth="${markerWidth}"
                        markerHeight="${markerHeight}"
                        markerUnits="${markerUnits}"
                        orient="auto-start-reverse">
                    <path ${strokeWidthAttr}
                        stroke="${stroke}"
                        stroke-linecap="${strokeLineCap}"
                        stroke-linejoin="${strokeLineJoin}"
                        stroke-miterlimit="1"
                        stroke-dasharray="${strokeDashArray}"
                        fill="${fill}"
                        d="M 0.5 0.5 L ${arrowWidth} ${arrowHeight / 2} L 0.5 ${arrowHeight}"/>
                </marker>`;
    }

    svgDefs;
    trustedSvgDefs;

    getSvgDefs() {
        const scaleFactor = this.layerUtilService.getPixelScaleFactor();
        if (scaleFactor === 0) {
            return '';
        }

        const mapBackgroundColor = this.spatialUtilService.getMapBackground();

        const showFeatures = this.layerUtilService.showFeatures();

        const defs = this.getAllLineSymbologyLookups()
            .filter(l => l.viewMode.toLowerCase() === mapBackgroundColor && (l.arrowLeft || l.arrowRight))
            .map(l => {
                const stroke = l.arrowColor && showFeatures ? `rgb(${l.arrowColor})` : 'none';
                const fill = l.arrowFillColor && showFeatures ? `rgb(${l.arrowFillColor})` : 'none';

                return [
                    this.createMarker(l, scaleFactor, stroke, fill, `line-marker-${l.type}`),
                    this.createMarker(l, scaleFactor, this.layerUtilService.getHoverColor(), fill, `line-marker-${l.type}-hover`),
                    this.createMarker(l, scaleFactor, this.layerUtilService.getHiddenColor(), fill, `hidden-line-marker-${l.type}`),
                    this.createMarker(l, scaleFactor, this.layerUtilService.getHiddenColor(), fill, `hidden-line-marker-${l.type}-hover`),
                    this.createMarker(l, scaleFactor, this.layerUtilService.getSelectedColor(), fill, `selected-line-marker-${l.type}`),
                    this.createMarker(l, scaleFactor, this.layerUtilService.getSelectedColor(), fill, `selected-line-marker-${l.type}-hover`)
                ].join('\n');
            });

        const newSvgDefs = defs.join('\n');
        if (newSvgDefs === this.svgDefs) {
            return this.trustedSvgDefs;
        }
        this.svgDefs = newSvgDefs;
        this.trustedSvgDefs = this.sanitizer.bypassSecurityTrustHtml(this.svgDefs);

        return this.trustedSvgDefs;
    }

    init() {
        const lineFeatureLayer = this.spatialUtilService.getLineMapLayer();

        this.layerUtilService.monkeyPatchFeatureLayerDrawShapeForPolylines(lineFeatureLayer);

        this.layerUtilService.addLayerEventHandler(lineFeatureLayer, 'graphic-node-add', evt => {
            let lineSymbologyLookup = this.getLineSymbologyLookup(evt.graphic.attributes.type);

            if (lineSymbologyLookup.description === 'Structure Clockwise Left' || lineSymbologyLookup.description === 'Structure Clockwise Right') {
                if (evt.startMapX > evt.endMapX) {
                    const description =
                        lineSymbologyLookup.description === 'Structure Clockwise Left' ? 'Structure Clockwise Right' : 'Structure Clockwise Left';
                    const lookupViewMode = this.spatialUtilService.getMapBackground() === 'white' ? 'White' : 'Black';
                    lineSymbologyLookup = this.lookupService
                        .getLookups('lineSymbology')
                        .filter(l => l.description === description && l.viewMode === lookupViewMode)[0];
                }
            }

            if (!lineSymbologyLookup) {
                evt.node.parentNode.removeChild(evt.node);
                return;
            }

            const featureId = evt.graphic.attributes.id;

            document
                .querySelectorAll(`${this.layerSelector} [${this.featureIdAttrName}="${featureId}"]`)
                .forEach(node => node.parentNode.removeChild(node));

            evt.node.setAttribute(this.featureTypeAttrName, `${evt.graphic.attributes.type}`);
            evt.node.setAttribute(this.featureIdAttrName, `${featureId}`);
            this.layerUtilService.setSelectedAttr(evt.node, this.featureClass, featureId);

            const lineLayerNode = document.getElementById(this.layerId);

            document.getElementById(this.layerId);

            const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
            g.setAttribute('id', this.layerUtilService.getFeatureNodeId(this.featureClass, featureId));
            g.setAttribute(this.featureIdAttrName, `${featureId}`);
            lineLayerNode.insertBefore(g, evt.node.nextSibling);

            const dashPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            this.updateDashPath(evt, dashPath);

            g.appendChild(dashPath);

            if (lineSymbologyLookup.svgSymbology) {
                const svgDoc = this.domParser.parseFromString(`<g>${lineSymbologyLookup.svgSymbology}</g>`, 'image/svg+xml');

                const patternId = `pattern-line-id-${featureId}`;

                const pattern = document.createElementNS('http://www.w3.org/2000/svg', 'pattern');
                pattern.setAttribute('id', patternId);
                pattern.setAttribute('patternUnits', 'userSpaceOnUse');
                pattern.setAttribute('x', '0');
                pattern.setAttribute('y', '0');

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const [viewBoxMinX, viewBoxMinY, viewBoxWidth, viewBoxHeight] = lineSymbologyLookup.svgViewBox.split(' ').map(v => parseFloat(v));
                pattern.setAttribute('viewBox', `0 0 ${viewBoxWidth} ${viewBoxHeight}`);

                svgDoc.childNodes[0].childNodes.forEach(childNode => {
                    const tagName = (childNode as Element).tagName;
                    const elt = document.createElementNS('http://www.w3.org/2000/svg', tagName);
                    for (let i = 0; i < (childNode as Element).attributes.length; i++) {
                        const attr = (childNode as Element).attributes[i];
                        elt.setAttribute(attr.name, attr.value);
                    }
                    pattern.appendChild(elt);
                });

                const patternPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                patternPath.setAttribute('fill', `url(#${patternId})`);

                this.updatePatternPath(evt, pattern, patternPath);

                g.appendChild(pattern);
                g.appendChild(patternPath);
            }
        });

        this.layerUtilService.addLayerEventHandler(lineFeatureLayer, 'graphic-node-remove',  (evt) => {
            const featureId = evt.graphic.attributes.id;
            const g = document.querySelector(`${this.layerSelector} > g[${this.featureIdAttrName}="${featureId}"]`);
            if (g) {
                g.parentNode.removeChild(g);
            }
        });

        this.layerUtilService.addLayerEventHandler(lineFeatureLayer, 'graphic-draw', (evt) => {
            const featureId = evt.graphic.attributes.id;

            this.layerUtilService.setHiddenAttr(evt.node, this.featureClass, featureId);

            this.updateDashPath(
                evt,
                document.querySelector(`${this.layerSelector} > g[${this.featureIdAttrName}="${featureId}"] > path:first-of-type`)
            );

            const pattern = document.getElementById(`pattern-line-id-${featureId}`);
            if (pattern) {
                this.updatePatternPath(evt, pattern, pattern.nextSibling);
            }
        });

        this.layerUtilService.addLayerEventHandler(lineFeatureLayer, 'edits-complete', () => {
            const {lineIds, lineTypes} = this.spatialUtilService.getLineGraphics().reduce(
                (acc, g) => {
                    acc.lineIds.push(g.attributes.id);
                    acc.lineTypes.push(g.attributes.type);
                    return acc;
                },
                {lineIds: [], lineTypes: []}
            );

            [...Array.from(document.querySelectorAll(`${this.layerSelector} > [${this.featureIdAttrName}]`))]
                .map(v => parseInt(v.getAttribute(this.featureIdAttrName)))
                .filter((v, i, a) => i === a.indexOf(v))
                .forEach(featureId => {
                    const lineIdx = lineIds.indexOf(featureId);
                    const path = document.querySelector(`${this.layerSelector} > path[${this.featureIdAttrName}="${featureId}"]`);

                    if (lineIdx === -1) {
                        const g = document.querySelector(`${this.layerSelector} > g[${this.featureIdAttrName}="${featureId}"]`);
                        if (g) {
                            g.parentNode.removeChild(g);
                        }
                        if (path) {
                            path.parentNode.removeChild(path);
                        }
                    } else if (path) {
                        const lineType = lineTypes[lineIdx];
                        const lineTypeAttrValue = parseInt(path.getAttribute(this.featureTypeAttrName));
                        if (lineTypeAttrValue !== lineType) {
                            path.setAttribute(this.featureTypeAttrName, `${lineType}`);
                        }
                    }
                });
        });

        this.layerUtilService.addLayerEventHandler(lineFeatureLayer, 'click', evt => {
            this.layerUtilService.onClick(this.featureClass, evt);
        });

        this.layerUtilService.addLayerEventHandler(lineFeatureLayer, 'dbl-click', evt => {
            this.layerUtilService.setSelectedAttr(evt.target, this.featureClass, evt.graphic.attributes.id);
        });

        this.layerUtilService.addLayerEventHandler(lineFeatureLayer, 'mouse-over', evt => {
            this.layerUtilService.onMouseOver(this.featureClass, evt);
        });

        this.layerUtilService.addLayerEventHandler(lineFeatureLayer, 'mouse-out', evt => {
            this.layerUtilService.onMouseOut(this.featureClass, evt);
        });

        return lineFeatureLayer;
    }
}
